import { type ReactElement, useMemo, useState } from "react";
import { type FilterData } from "~/features/cms/components/cmsFilterableTile/CMSFilterableTileContainer";
import { trackFilterToggle } from "~/app/analytics/ga/contentStream";
import { type CMSFilterableTileContainerFilterDTO } from "~/features/cms/components/cmsFilterableTile/dto";
import { cleanupText } from "~/features/cms/utils/markup";
import classNames from "classnames";
import CheckIcon from "@mui/icons-material/Check";

export interface FilterTileProps extends Omit<CMSFilterableTileContainerFilterDTO, "type" | "tags"> {
  className?: string;
  active: boolean;
  onClick: () => void;
}

interface Props<T extends { tags?: string }> {
  allTiles: T[];
  showAllLabel?: string;

  filters: CMSFilterableTileContainerFilterDTO[];
}

export function useTileFilter<T extends { tags?: string }>({ filters, allTiles, showAllLabel }: Props<T>) {
  const [activeFilters, setActiveFilters] = useState<FilterData[]>([]);

  const { clear, toggle } = useMemo(
    () => ({
      clear: () => setActiveFilters([]),
      toggle: (filter: FilterData) =>
        setActiveFilters((prev) => {
          let newFilters = prev.filter((item) => item != filter);
          if (prev.length === newFilters.length) {
            newFilters = prev.concat(filter);
          }

          trackFilterToggle(newFilters);

          return newFilters;
        }),
    }),
    [],
  );

  const filterTiles = useMemo(() => {
    const filterTiles: ReactElement<FilterTileProps>[] = [];

    if (showAllLabel) {
      filterTiles.push(
        <FilterTile key={"all"} label={showAllLabel} onClick={clear} active={activeFilters.length === 0} />,
      );
    }

    filters.forEach((filter, index) => {
      filterTiles.push(
        <FilterTile
          key={index}
          label={filter.label}
          onClick={() => toggle(filter)}
          active={activeFilters.includes(filter)}
        />,
      );
    });

    return filterTiles;
  }, [activeFilters, filters, showAllLabel, toggle, clear]);

  const visibleTiles = useMemo(() => {
    let visibleTiles;
    if (activeFilters.length === 0) {
      visibleTiles = allTiles.map((t, index) => ({ ...t, index }));
    } else {
      const visibleTags = new Set(
        activeFilters
          .map((filter) => filter.tags ?? "")
          .map((str) => str.split(","))
          .flat(),
      );

      visibleTiles = allTiles
        .map((t, index) => ({ ...t, index }))
        .filter((tile) => (tile.tags ?? "").split(",").some((tag) => visibleTags.has(tag)));
    }

    return visibleTiles;
  }, [activeFilters, allTiles]);

  return {
    visibleTiles,
    filterContainer: <FilterContainer filters={filterTiles} />,
  };
}

interface FilterContainerProps {
  className?: string;
  filters: ReactElement<unknown>[];
}

const FilterContainer = ({ filters, className }: FilterContainerProps) => {
  if (!filters.length) {
    return null;
  }

  return (
    <div className={classNames("scha__cms__filterable-container__filters", className)}>
      <div className="scha__cms__filterable-container__filters__row">{filters}</div>
    </div>
  );
};

const FilterTile = ({ className, active, onClick, label }: FilterTileProps) => {
  label = cleanupText(label);

  const cls = classNames(
    "scha__cms__filterable-container__filter copy_left",
    className,
    active && "scha__cms__filterable-container__filter--active",
  );
  return (
    <div className={cls} onClick={onClick}>
      {active && <CheckIcon className={"icon-margin"}  />}
      {label}
    </div>
  );
};
